import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";
import { useProduct } from "@/components/pdp/PdpDataContext";

export const StickyCartBar = ({ variant }) => {
  const P = useProduct();
  const isV3 = !!P.v3;
  const [visible, setVisible] = useState(false);
  const [invert, setInvert] = useState(false);
  const v = P.variants.find((x) => x.id === variant);
  const name = P.titleLines.join(" ");

  useEffect(() => {
    const onScroll = () => {
      const hero = document.querySelector('[data-testid="hero-section"]');
      if (!hero) return;
      const bottom = hero.getBoundingClientRect().bottom;
      setVisible(bottom < 0 && window.scrollY < document.body.scrollHeight - window.innerHeight - 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!isV3) return;
    const targets = Array.from(document.querySelectorAll('[data-inverted-bg="blue"]'));
    if (!targets.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        setInvert(() => {
          const map = new Map(entries.map((e) => [e.target, e.isIntersecting]));
          return targets.some((t) => (map.has(t) ? map.get(t) : t.getBoundingClientRect().top < window.innerHeight && t.getBoundingClientRect().bottom > window.innerHeight - 120));
        });
      },
      {
        rootMargin: `-${window.innerHeight - 120}px 0px 0px 0px`,
        threshold: 0,
      }
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, [isV3, visible]);

  const useLight = !isV3 || invert;

  const barBg = useLight
    ? "bg-white/95 border-t border-neutral-200 shadow-[0_-8px_32px_rgba(0,0,0,0.05)]"
    : "bg-riversol-blue/95 border-t border-white/10 shadow-[0_-8px_32px_rgba(11,37,69,0.35)]";
  const thumbFrame = useLight ? "bg-riversol-sand" : "bg-white/10";
  const titleColor = useLight ? "text-neutral-900" : "text-white";
  const subColor = useLight ? "text-neutral-500" : "text-white/70";
  const priceColor = useLight ? "text-neutral-900" : "text-white";
  const starColor = useLight ? "fill-riversol-blue text-riversol-blue" : "fill-white text-white";
  const ctaClass = useLight
    ? "bg-riversol-blue text-white hover:bg-riversol-blueLight"
    : "bg-white text-riversol-blue hover:bg-riversol-sand";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className={`fixed bottom-0 left-0 right-0 z-50 backdrop-blur-md transition-colors duration-500 ${barBg}`}
          data-testid="sticky-cart-bar"
          data-inverted={useLight ? "light" : "blue"}
        >
          <div className="max-w-[1600px] mx-auto px-4 lg:px-8 h-[64px] lg:h-[76px] flex items-center justify-between gap-3 lg:gap-6">
            <div className="flex items-center gap-3 lg:gap-4 min-w-0">
              <div className={`w-10 h-10 lg:w-12 lg:h-12 editorial-frame shrink-0 transition-colors duration-500 ${thumbFrame}`}>
                <img src={P.productThumb} alt="" className="w-full h-full object-cover" />
              </div>
              {/* Desktop-only product info; mobile bar is thumb + price + CTA only */}
              <div className="hidden lg:block min-w-0">
                <div className={`text-sm font-medium leading-tight truncate transition-colors duration-500 ${titleColor}`}>{name}</div>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className={`text-xs font-mono transition-colors duration-500 ${subColor}`} data-testid="sticky-variant">{v?.label}</span>
                  <span className={`flex items-center gap-1 text-xs transition-colors duration-500 ${subColor}`}>
                    <Star size={12} className={`transition-colors duration-500 ${starColor}`} /> {P.rating}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 lg:gap-6 shrink-0">
              <span className={`font-mono text-base lg:text-lg transition-colors duration-500 ${priceColor}`}>{v?.price}</span>
              <button
                data-testid="sticky-shop-now"
                className={`text-[11px] lg:text-xs tracking-[0.08em] lg:tracking-[0.12em] uppercase font-medium px-5 lg:px-8 py-3 lg:py-3.5 ${P.v32 ? "" : "rounded-full"} whitespace-nowrap transition-colors duration-500 ${ctaClass}`}
              >
                {P.ctaLabel}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StickyCartBar;
